import { useContext, useState, createContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Skeleton from 'react-loading-skeleton';
import mixpanel from 'mixpanel-browser';
import EVENTS from '../../analytics/events';
import { Modal } from '@platform/components';
import { UserNextBestActions } from '@platform/client';
import OrgUserNextBestActions from './OrgUserNextBestActions';
import ProUserNextBestActions from './ProUserNextBestActions';
import {
  GetCurrentUser200,
  OrgUserNextBestActionResponse,
  ProUserNextBestActionResponse,
} from 'lib/client/src/platformapi.schemas';

export const NbaModalsContext = createContext({
  dismissNbaModelIsOpen: false,
  setDismissNbaModelIsOpen: (_open: boolean) => {},
  dismissingEngagementId: '',
  setDismissingEngagementId: (_id: string) => {},
});

// possibly move this to own file
const DismissNbaModel = () => {
  const { dismissNbaModelIsOpen, setDismissNbaModelIsOpen, dismissingEngagementId } =
    useContext(NbaModalsContext);

  const queryClient = useQueryClient();

  const dismissOnboardingVideo = UserNextBestActions.useDismissOnboardVideo({
    mutation: {
      onSettled: async () => {
        return await queryClient.invalidateQueries({
          queryKey: UserNextBestActions.getGetUserNextBestActionsQueryKey(),
        });
      },
    },
  });

  return (
    <Modal
      variant='dialog'
      headerDivision='divider'
      title='Are you sure you want to dismiss this action?'
      isOpen={dismissNbaModelIsOpen}
      onClose={() => {
        setDismissNbaModelIsOpen(false);
      }}
      primaryCTA={{
        text: 'Yes, dismiss',
        callback: () => {
          // All nbas dismissed currently in browser session only, with exception of onboard video.
          // I suspect thi will actually be local storage but for now ask was when tab closes
          sessionStorage.setItem(dismissingEngagementId, 'dismissed');
          // dimsiss onboard video is 'permanent' and stored in db.
          if (dismissingEngagementId === 'onboarding_video') {
            dismissOnboardingVideo.mutate();
          }
          setDismissNbaModelIsOpen(false);
          mixpanel.track(EVENTS.DASHBOARD.HOME.NBA_DISMISS_MODAL_CONFIRMED, {
            engagementActionId: dismissingEngagementId,
          });
        },
      }}
      secondaryCTA={{
        text: 'Close',
        callback: () => {
          setDismissNbaModelIsOpen(false);
          mixpanel.track(EVENTS.DASHBOARD.HOME.NBA_DISMISS_MODAL_CANCELLED, {
            engagementActionId: dismissingEngagementId,
          });
        },
      }}
    >
      <div />
    </Modal>
  );
};

/** A list of user "Engagments" shown as cards */
const NextBestActions = (props: { user: GetCurrentUser200; cardsToDisplay?: number }) => {
  const { cardsToDisplay = 3, user } = props;
  const { data: userNbas, isSuccess, isLoading } = UserNextBestActions.useGetUserNextBestActions();

  const [dismissNbaModelIsOpen, setDismissNbaModelIsOpen] = useState(false);
  const [dismissingEngagementId, setDismissingEngagementId] = useState('');

  if (!user) {
    return null;
  }
  const accountType = user.accountType;

  return (
    <NbaModalsContext.Provider
      value={{
        dismissNbaModelIsOpen: dismissNbaModelIsOpen,
        setDismissNbaModelIsOpen: setDismissNbaModelIsOpen,
        dismissingEngagementId,
        setDismissingEngagementId,
      }}
    >
      <div className='flex flex-col gap-4'>
        {isLoading && (
          <div className='bg-white shadow rounded-lg p-4 border-l-4 relative'>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </div>
        )}
        {isSuccess && accountType === 'organization' && user.organization?.id && (
          <OrgUserNextBestActions
            {...(userNbas.actions as OrgUserNextBestActionResponse)}
            cardsToDisplay={cardsToDisplay}
            organizationId={user.organization.id}
          />
        )}
        {isSuccess && accountType === 'experteer' && (
          <ProUserNextBestActions
            {...(userNbas.actions as ProUserNextBestActionResponse)}
            cardsToDisplay={cardsToDisplay}
          />
        )}
        <DismissNbaModel />
      </div>
    </NbaModalsContext.Provider>
  );
};

export default NextBestActions;
